import { useState } from "react";
import { supabase } from "../../supabase";
import { GEAR_CATEGORIES } from "../../constants";
import Field from "../../components/Field";

function parseItem(str) {
  const idx = str.lastIndexOf("::");
  if (idx === -1) return { name: str, qty: 1 };
  return { name: str.slice(0, idx), qty: parseInt(str.slice(idx + 2)) || 1 };
}

export default function ContributionForm({ eventId, existingNames, onSave, onCancel, contribution, categoryItems, neededItems }) {
  const [name, setName] = useState(contribution?.contributor_name || "");
  const [quantities, setQuantities] = useState(() => {
    if (!contribution) return {};
    const known = new Set(GEAR_CATEGORIES.flatMap(c => (categoryItems?.[c.id]) || c.items));
    const result = {};
    for (const item of (contribution.items || [])) {
      const { name: n, qty } = parseItem(item);
      if (known.has(n)) result[n] = qty;
    }
    return result;
  });
  const [custom,  setCustom]  = useState({});
  const [notes,   setNotes]   = useState(contribution?.notes || "");
  const [saving,  setSaving]  = useState(false);

  function toggle(item) {
    setQuantities(prev => {
      if (prev[item] !== undefined) {
        const next = { ...prev };
        delete next[item];
        return next;
      }
      return { ...prev, [item]: 1 };
    });
  }

  function changeQty(item, delta) {
    setQuantities(prev => {
      const newQty = (prev[item] || 1) + delta;
      if (newQty <= 0) {
        const next = { ...prev };
        delete next[item];
        return next;
      }
      return { ...prev, [item]: newQty };
    });
  }

  async function save() {
    if (!name.trim()) return alert("Escribe tu nombre.");
    if (existingNames.includes(name.trim().toLowerCase())) return alert("Ya hay un aporte con ese nombre.");
    const customItems   = Object.values(custom).map(v => v.trim().replace(/::/g, "")).filter(Boolean);
    const selectedItems = Object.entries(quantities).map(([item, qty]) => qty > 1 ? `${item}::${qty}` : item);
    const allItems      = [...selectedItems, ...customItems];
    if (allItems.length === 0) return alert("Selecciona o escribe al menos un item.");
    setSaving(true);
    if (contribution?.id) {
      await supabase.from("contributions").update({
        contributor_name: name.trim(),
        items: allItems,
        notes: notes.trim() || null,
      }).eq("id", contribution.id);
    } else {
      await supabase.from("contributions").insert({
        event_id: eventId,
        contributor_name: name.trim(),
        items: allItems,
        notes: notes.trim() || null,
      });
    }
    setSaving(false);
    onSave();
  }

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <h3 style={{ fontSize: 13, letterSpacing: 2, textTransform: "uppercase", color: "#aaa", marginBottom: 20 }}>
        {contribution ? "Editar aporte" : "¿Qué traes?"}
      </h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <Field label="Tu nombre *">
          <input className="input" placeholder="Ej. Héctor" value={name} onChange={e => setName(e.target.value)} />
        </Field>

        {GEAR_CATEGORIES.map(cat => {
          const items = (categoryItems?.[cat.id]) || cat.items;
          return (
            <div key={cat.id}>
              <div style={{ fontSize: 12, letterSpacing: 2, textTransform: "uppercase", color: "#999", marginBottom: 10, display: "flex", gap: 8 }}>
                {cat.icon} {cat.label}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 8 }}>
                {items.map(item => {
                  const qty        = quantities[item];
                  const isSelected = qty !== undefined;
                  const isNeeded   = !isSelected && neededItems?.includes(item);
                  return (
                    <div key={item} style={{ display: "flex", alignItems: "stretch", flexDirection: "column", gap: 2 }}>
                      <div style={{ display: "flex", alignItems: "stretch" }}>
                      <button
                        onClick={() => toggle(item)}
                        style={{
                          background:  isSelected ? "#cc2200" : "#141414",
                          border:      `1px solid ${isSelected ? "#cc2200" : isNeeded ? "#ffd600" : "#2a2a2a"}`,
                          color:       isSelected ? "#fff" : isNeeded ? "#ffd600" : "#aaa",
                          borderRadius: isSelected ? "4px 0 0 4px" : 4,
                          padding: "6px 12px", fontSize: 12,
                          cursor: "pointer", transition: "all 0.15s", fontFamily: "inherit",
                          fontWeight: isSelected || isNeeded ? 700 : 400,
                        }}
                      >
                        {item}
                      </button>
                      {isSelected && (
                        <div style={{ display: "flex", alignItems: "center", background: "#1a0800", border: "1px solid #cc2200", borderLeft: "none", borderRadius: "0 4px 4px 0" }}>
                          <button onClick={() => changeQty(item, -1)} style={{ background: "none", border: "none", color: "#cc2200", cursor: "pointer", padding: "0 7px", fontSize: 16, lineHeight: 1, fontFamily: "inherit" }}>−</button>
                          <span style={{ fontSize: 12, color: "#fff", minWidth: 16, textAlign: "center", fontWeight: 700 }}>{qty}</span>
                          <button onClick={() => changeQty(item, 1)} style={{ background: "none", border: "none", color: "#cc2200", cursor: "pointer", padding: "0 7px", fontSize: 16, lineHeight: 1, fontFamily: "inherit" }}>+</button>
                        </div>
                      )}
                      </div>
                      {isNeeded && (
                        <span style={{ fontSize: 9, color: "#ffd600", letterSpacing: 0.5, textAlign: "center" }}>⚠ prioritario</span>
                      )}
                    </div>
                  );
                })}
              </div>
              <input
                className="input"
                placeholder={`Otro ${cat.label.toLowerCase()}...`}
                value={custom[cat.id] || ""}
                onChange={e => setCustom({ ...custom, [cat.id]: e.target.value })}
                style={{ fontSize: 12, padding: "8px 12px" }}
              />
            </div>
          );
        })}

        <Field label="Notas adicionales (opcional)">
          <textarea
            className="input"
            rows={3}
            placeholder="Ej. el monitor solo tiene HDMI, traigo controladores extra..."
            value={notes}
            onChange={e => setNotes(e.target.value)}
            style={{ resize: "vertical" }}
          />
        </Field>

        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn-primary" style={{ flex: 1, padding: 14 }} onClick={save} disabled={saving}>
            {saving ? "Guardando..." : contribution ? "💾 Guardar cambios" : "✅ Confirmar aporte"}
          </button>
          <button className="btn-ghost" onClick={onCancel}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}
